import type { FmProductSectionDescriptor } from '@/components/ui/FmProductPage';
import type { Language } from '@/i18n';

const EN_SCENES = [
  [
    '00:00–00:05',
    'A future you can see and shape',
    'Forest inventory becomes a visible digital forest.',
  ],
  [
    '00:05–00:12',
    'Explore your forest’s future',
    'Simulate alternative paths and compare how the forest changes over time.',
  ],
  [
    '00:12–00:18',
    'One plan, your priorities',
    'Refine the plan through income, forest-cover, and risk lenses.',
  ],
  [
    '00:18–00:25',
    'Check the data behind the plan',
    'Compare source inputs with field measurements, review gaps, and simulate again.',
  ],
  [
    '00:25–00:34',
    'Your plan in the conversation',
    'Explore a proposed plan change through an illustrated ChatGPT and Lehti AI connection.',
  ],
  [
    '00:34–00:40',
    'Ready for real offers',
    'Export the reviewed plan and request offers on the same basis.',
  ],
  [
    '00:40–00:49',
    'Every stem, every offer',
    'Compare buyer terms with stem shapes and diameter distributions.',
  ],
  [
    '00:49–00:57',
    'See which offer comes out ahead',
    'Review an illustrative modelled net-return comparison and the reason for its result.',
  ],
  [
    '00:57–01:02',
    'Understand more, decide with clarity',
    'You review the terms and make the final decision.',
  ],
] as const;

const FI_SCENES = [
  [
    '00:00–00:05',
    'Näe metsäsi tulevaisuus',
    'Metsävaratiedoista muodostuu näkyvä digitaalinen metsä.',
  ],
  [
    '00:05–00:12',
    'Tutki tulevia polkuja',
    'Simuloi vaihtoehtoja ja vertaa, miten metsä kehittyy ajan kuluessa.',
  ],
  [
    '00:12–00:18',
    'Yksi suunnitelma, omat tavoitteesi',
    'Tarkenna suunnitelmaa tulojen, puuston peittävyyden ja riskin näkökulmista.',
  ],
  [
    '00:18–00:25',
    'Tarkista tiedot suunnitelman takaa',
    'Vertaa lähtötietoja maastomittauksiin, tarkista aukot ja simuloi uudelleen.',
  ],
  [
    '00:25–00:34',
    'Suunnitelmasi osana keskustelua',
    'Tutki ehdotettua muutosta havainnollistetun ChatGPT- ja Lehti AI -yhteyden avulla.',
  ],
  [
    '00:34–00:40',
    'Valmiina tarjouspyyntöön',
    'Vie tarkistettu suunnitelma ja pyydä tarjoukset samoilla lähtötiedoilla.',
  ],
  [
    '00:40–00:49',
    'Jokainen runko, jokainen tarjous',
    'Vertaa ostajien ehtoja runkomuotoihin ja läpimittajakaumiin.',
  ],
  [
    '00:49–00:57',
    'Näe, mikä tarjous nousee kärkeen',
    'Tarkista havainnollistava mallinnetun nettotuoton vertailu ja tuloksen perusteet.',
  ],
  [
    '00:57–01:02',
    'Ymmärrä enemmän, päätä selkeämmin',
    'Tarkistat ehdot ja teet lopullisen päätöksen itse.',
  ],
] as const;

export function planJourneyFilmSection(locale: Language): FmProductSectionDescriptor {
  const finnish = locale === 'fi';
  const scenes = finnish ? FI_SCENES : EN_SCENES;
  return {
    id: 'landing-plan-journey-film',
    capability: 'registered-media',
    mediaKind: 'video',
    role: 'lead',
    title: finnish ? 'Metsätiedosta päätökseen' : 'From forest insight to action',
    state: { status: 'ready' },
    answer: finnish
      ? 'Metsätiedosta suunnitelmaksi. Suunnitelmasta päätökseksi.'
      : 'From forest insight to a plan. From a plan to your decision.',
    explanation: finnish
      ? 'Tutki vaihtoehdot, tarkista lähtötiedot ja vie oma suunnitelmasi tarjousten vertailuun.'
      : 'Explore alternatives, check the inputs, and carry your plan into offer comparison.',
    caption: finnish
      ? 'Havainnollistava video · 62 s · videon luvut ovat esimerkkejä'
      : 'Illustrative film · 62 sec · concept data is illustrative',
    mediaIdentity: finnish ? 'product.plan-journey.fi' : 'product.plan-journey.en',
    posterIdentity: finnish ? 'product.plan-journey.poster-fi' : 'product.plan-journey.poster-en',
    playLabel: finnish ? 'Toista havainnollistava video' : 'Play the illustrative film',
    pauseLabel: finnish ? 'Keskeytä video' : 'Pause the film',
    transcriptLabel: finnish ? 'Lue videon sisältö' : 'Read the film transcript',
    transcriptTitle: finnish
      ? 'Metsästä suunnitelmaan ja tarjousten vertailuun'
      : 'From forest to plan and offer comparison',
    transcriptScenes: scenes.map(([timeLabel, title, detail], index) => ({
      id: `plan-film-scene-${index + 1}`,
      timeLabel,
      title,
      detail,
    })) as [
      { id: string; timeLabel: string; title: string; detail: string },
      ...{ id: string; timeLabel: string; title: string; detail: string }[],
    ],
  };
}
